import { useEffect, useState } from "react"
import type { FormEvent } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"
import { traAdmin, type TraCertificationEdition, type TraCertLevel } from "@/lib/api/traAdminClient"
import { EditionShell, type EditionTabKey } from "@/components/tra/EditionShell"
import { WaitlistTable } from "@/components/tra/WaitlistTable"
import { StyledSelect, CheckRow, inputCls, labelCls, btnSave } from "../../components/admin/FormComponents"
import { LoadState } from "@/components/ui/LoadState"

type Seedbed = { id: string; name: string; institution_name?: string }

const CERT_LEVELS: TraCertLevel[] = ["L1", "L2", "L3", "TMP"]

function isOpen(edition: Pick<TraCertificationEdition, "registrationStartDate" | "registrationDeadline">): boolean {
  const now = Date.now()
  return now >= new Date(edition.registrationStartDate).getTime() && now <= new Date(edition.registrationDeadline).getTime()
}

function toIso(date: string): string | undefined {
  if (!date) return undefined
  return new Date(`${date}T00:00:00.000Z`).toISOString()
}

function toDateInput(value?: string | null): string {
  if (!value) return ""
  return value.slice(0, 10)
}

type InfoForm = {
  name: string
  registrationStartDate: string
  registrationDeadline: string
  certLevels: TraCertLevel[]
}

function EditionInfoForm({
  edition,
  onCreated,
  onSaved,
}: {
  edition: TraCertificationEdition | null
  onCreated: (created: TraCertificationEdition) => void
  onSaved: () => void
}) {
  const isEdit = Boolean(edition)
  const [form, setForm] = useState<InfoForm>({
    name: edition?.name ?? "",
    registrationStartDate: toDateInput(edition?.registrationStartDate),
    registrationDeadline: toDateInput(edition?.registrationDeadline),
    certLevels: edition?.certLevels ?? ["L1"],
  })
  const [error, setError] = useState("")

  const mutation = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name,
        registrationStartDate: toIso(form.registrationStartDate)!,
        registrationDeadline: toIso(form.registrationDeadline)!,
        certLevels: form.certLevels,
      }
      return edition ? traAdmin.updateEdition(edition.id, payload) : traAdmin.createEdition(payload)
    },
    onSuccess: (saved) => {
      setError("")
      if (edition) onSaved()
      else onCreated(saved)
    },
    onError: (err) => setError(getErrorMessage(err)),
  })

  function toggleLevel(level: TraCertLevel) {
    setForm((f) => ({
      ...f,
      certLevels: f.certLevels.includes(level) ? f.certLevels.filter((l) => l !== level) : [...f.certLevels, level],
    }))
  }

  return (
    <form onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); mutation.mutate() }}>
      <div className="space-y-6 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Identidad</h3>
            <div>
              <label className={labelCls}>Nombre de la edición *</label>
              <input
                required
                autoFocus
                placeholder="Ej. Certificación marzo 2026"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Niveles que certifica *</label>
              <div className="space-y-1">
                {CERT_LEVELS.map((level) => (
                  <CheckRow
                    key={level}
                    checked={form.certLevels.includes(level)}
                    onChange={() => toggleLevel(level)}
                    label={level}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-3 lg:col-span-2">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Ventana de inscripción</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className={labelCls}>Apertura *</label>
                <input
                  required
                  type="date"
                  value={form.registrationStartDate}
                  onChange={(e) => setForm((f) => ({ ...f, registrationStartDate: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Cierre *</label>
                <input
                  required
                  type="date"
                  value={form.registrationDeadline}
                  onChange={(e) => setForm((f) => ({ ...f, registrationDeadline: e.target.value }))}
                  className={inputCls}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {error ? (
        <p role="alert" aria-live="assertive" className="mt-5 text-sm font-medium text-error-500">
          {error}
        </p>
      ) : null}

      <div className="mt-5 flex justify-end gap-2 border-t border-gray-100 pt-5 dark:border-gray-800">
        <button type="submit" disabled={mutation.isPending || form.certLevels.length === 0} className={btnSave}>
          {mutation.isPending ? "Guardando..." : isEdit ? "Guardar" : "Crear edición"}
        </button>
      </div>
    </form>
  )
}

function EditionSeedbedSection({ edition, onSaved }: { edition: TraCertificationEdition; onSaved: () => void }) {
  const [seedbeds, setSeedbeds] = useState<Seedbed[]>([])
  const [seedbedId, setSeedbedId] = useState(edition.seedbedId ?? "")
  const [error, setError] = useState("")

  useEffect(() => {
    api
      .get("/academy/seedbeds/admin?limit=200&page=1")
      .then((res) => setSeedbeds(res.data?.data?.seedbeds ?? []))
      .catch(() => setSeedbeds([]))
  }, [])

  const mutation = useMutation({
    mutationFn: () => traAdmin.updateEdition(edition.id, { seedbedId: seedbedId || null }),
    onSuccess: () => { setError(""); onSaved() },
    onError: (err) => setError(getErrorMessage(err)),
  })

  return (
    <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-white/90">Semillero de aprendizaje</h3>
      <p className="text-xs text-gray-500 dark:text-gray-400">
        Los candidatos aprobados en esta edición quedan inscritos automáticamente en el semillero seleccionado.
        Déjalo vacío si esta edición no tiene contenido de aprendizaje asociado todavía.
      </p>
      <StyledSelect value={seedbedId} onChange={(e) => setSeedbedId(e.target.value)}>
        <option value="">Sin semillero asignado</option>
        {seedbeds.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
            {s.institution_name ? ` — ${s.institution_name}` : ""}
          </option>
        ))}
      </StyledSelect>
      {error ? (
        <p role="alert" aria-live="assertive" className="text-sm font-medium text-error-500">
          {error}
        </p>
      ) : null}
      <div className="flex justify-end">
        <button type="button" onClick={() => mutation.mutate()} disabled={mutation.isPending} className={btnSave}>
          {mutation.isPending ? "Guardando..." : "Guardar"}
        </button>
      </div>
    </div>
  )
}

export default function EditionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const isEdit = Boolean(id)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = (searchParams.get("tab") as EditionTabKey | null) ?? "info"

  const { data: edition, isLoading } = useQuery({
    queryKey: ["tra-edition", id],
    queryFn: () => traAdmin.getEdition(id!),
    enabled: isEdit,
  })

  function setTab(next: EditionTabKey) {
    setSearchParams(next === "info" ? {} : { tab: next })
  }

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["tra-edition", id] })
    qc.invalidateQueries({ queryKey: ["server-table", "/tra/certification-editions"] })
  }

  return (
    <EditionShell
      mode={isEdit ? "edit" : "create"}
      activeTab={tab}
      onTabChange={setTab}
      breadcrumbs={[
        { label: "SateLab", to: "/" },
        "TRA",
        { label: "Ediciones", to: "/tra/editions" },
        isEdit ? (edition?.name ?? "Cargando...") : "Nueva",
      ]}
      title={isEdit ? edition?.name ?? "Cargando..." : "Nueva edición de certificación"}
      subtitle="Ciclo de certificación Tripoli Colombia con su propia ventana de inscripción y semillero de aprendizaje"
      metrics={
        edition
          ? [
              { label: "Candidatos", value: String(edition.waitlist_count ?? 0) },
              { label: "Niveles", value: edition.certLevels.join(", ") },
              {
                label: "Inscripción",
                value: isOpen(edition) ? "Abierta" : "Cerrada",
                tone: isOpen(edition) ? "good" : "neutral",
              },
            ]
          : []
      }
    >
      {isEdit && isLoading ? (
        <LoadState />
      ) : (
        <>
          {tab === "info" && (
            <EditionInfoForm
              edition={edition ?? null}
              onCreated={(created) => {
                qc.invalidateQueries({ queryKey: ["server-table", "/tra/certification-editions"] })
                navigate(`/tra/editions/${created.id}`)
              }}
              onSaved={invalidate}
            />
          )}
          {tab === "participantes" && edition && <WaitlistTable editionId={edition.id} />}
          {tab === "configuracion" && edition && <EditionSeedbedSection edition={edition} onSaved={invalidate} />}
        </>
      )}
    </EditionShell>
  )
}
